import {
  printingsForCardGame,
  TCG_CARD_GAMES,
  TCG_IMPORT_ISSUE_MESSAGES,
  type TcgImportResult,
} from "@loot-os/fulfillment-core/catalog/tcg-import";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Field, FieldDescription, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import { toast } from "@loot-os/ui/components/sonner";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import {
  type CatalogBulkImportRow,
  type ParsedCatalogBulkImport,
  parseCatalogBulkImport,
} from "../lib/catalogItemBulkImport";
import { useTRPC } from "../lib/trpc";

interface PreviewRow {
  line: number;
  name: string;
  productId: string;
  cardGame: string;
  printing: string;
  result: string;
  id: string;
}

const previewColumns = createDataTableColumns<PreviewRow>();

const columns = previewColumns.columns([
  previewColumns.accessor("line", {
    header: "Line",
    size: 80,
    meta: { align: "end", filter: "number" },
  }),
  previewColumns.accessor("name", {
    header: "Asset group name",
    meta: { filter: "text" },
  }),
  previewColumns.accessor("productId", {
    header: "Product id",
    meta: { filter: "text" },
  }),
  previewColumns.accessor("cardGame", {
    header: "Card game",
    meta: { filter: "text" },
  }),
  previewColumns.accessor("printing", {
    header: "Card printing",
    meta: { filter: "text" },
  }),
  previewColumns.accessor("result", {
    header: "Result",
    meta: { filter: "text" },
    cell: ({ getValue }) => {
      const value = getValue();
      const failed = value !== "Ready" && value !== "Created";
      return <span className={failed ? "text-destructive" : undefined}>{value}</span>;
    },
  }),
  previewColumns.accessor("id", {
    header: "Id",
    meta: { filter: "text", csv: (value) => (typeof value === "string" ? value : "") },
    cell: ({ getValue }) => <span className="font-mono text-xs">{getValue()}</span>,
  }),
]);

function resultText(row: CatalogBulkImportRow, outcome: TcgImportResult | undefined): string {
  if (outcome) {
    return outcome.outcome === "created" ? "Created" : TCG_IMPORT_ISSUE_MESSAGES[outcome.outcome];
  }
  return row.issue ? TCG_IMPORT_ISSUE_MESSAGES[row.issue] : "Ready";
}

/** CSV import from the boxed TCG Asset Group Creator: one card per row. */
export function CatalogItemBulkImport() {
  const trpc = useTRPC();
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedCatalogBulkImport | null>(null);
  const [results, setResults] = useState<TcgImportResult[] | null>(null);

  const importRows = useMutation(
    trpc.platform.fulfillment.items.bulkCreate.mutationOptions({
      onSuccess: (created) => {
        const count = created.filter((row) => row.outcome === "created").length;
        const failed = created.length - count;
        toast.success(failed === 0 ? `Created ${count}.` : `Created ${count}. ${failed} failed.`);
        setResults(created);
      },
    }),
  );

  const byIndex = new Map(results?.map((result) => [result.index, result]));
  const preview: PreviewRow[] =
    parsed?.rows.map((row, index) => {
      const outcome = byIndex.get(index);
      return {
        line: row.line,
        name: row.name,
        productId: row.productId,
        cardGame: row.cardGame,
        printing: row.printing,
        result: resultText(row, outcome),
        id: outcome?.id ?? "",
      };
    }) ?? [];

  const blocked = !parsed || parsed.fileIssues.length > 0 || parsed.rows.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk import</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="catalog-bulk-file">CSV</FieldLabel>
          <FieldDescription>
            The TCG Asset Group Creator's file. Columns are Asset Group Name, Product ID, Card Game
            and Card Printing. Each row becomes a TCGplayer item tagged with its game. A product id
            and printing already in the catalog is skipped.
          </FieldDescription>
          <Input
            id="catalog-bulk-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              void file.text().then((text) => {
                setFileName(file.name);
                setParsed(parseCatalogBulkImport(text));
                setResults(null);
              });
            }}
          />
        </Field>
        <p className="text-sm text-muted-foreground">
          Card game is one of {TCG_CARD_GAMES.join(", ")}. Pokemon printings:{" "}
          {printingsForCardGame("POKEMON").join(", ")}. Lorcana:{" "}
          {printingsForCardGame("LORCANA").join(", ")}. Yu-Gi-Oh:{" "}
          {printingsForCardGame("YUGIOH").join(", ")}. Every other game:{" "}
          {printingsForCardGame("MTG").join(", ")}.
        </p>
        {fileName ? <p className="text-sm">{fileName}</p> : null}
        {parsed?.fileIssues.map((issue) => (
          <p key={issue} className="text-sm text-destructive" role="alert">
            {issue}
          </p>
        ))}
        <div>
          <Button
            type="button"
            disabled={blocked || importRows.isPending}
            onClick={() => {
              if (!parsed || parsed.rows.length === 0) return;
              importRows.mutate({
                rows: parsed.rows.map((row) => ({
                  name: row.name,
                  productId: row.productId,
                  cardGame: row.cardGame,
                  printing: row.printing,
                })),
              });
            }}
          >
            {importRows.isPending ? "Importing…" : "Bulk import"}
          </Button>
        </div>
        {preview.length > 0 ? (
          <DataTable
            columns={columns}
            data={preview}
            getRowId={(row) => String(row.line)}
            csvName="catalog-item-import"
            maxHeight="24rem"
            search="Search rows"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
